import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarCheck, Phone, MapPin, CheckCircle2 } from 'lucide-react';

// Public reservation page — accessible without login. Customer-facing.
export default function PublicReserve() {
  const [params] = useSearchParams();
  const langParam = params.get('lang') || (navigator.language?.startsWith('pt') ? 'pt' : 'es');
  const [info, setInfo] = useState(null);
  const [locations, setLocations] = useState([]);
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    cliente_nombre: '', cliente_telefono: '', cliente_email: '',
    fecha: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    hora: '21:00',
    personas: 2, notas: '', location_id: '',
  });

  // Stable translator (depends only on langParam)
  const t = useCallback((es, pt) => (langParam === 'pt' ? pt : es), [langParam]);

  const init = useCallback(async () => {
    try {
      const [i, locs] = await Promise.all([
        api.get('/public/info'),
        api.get('/public/locations'),
      ]);
      setInfo(i.data);
      setLocations(locs.data);
      const principal = locs.data.find(l => l.es_principal) || locs.data[0];
      if (principal) setForm(f => ({ ...f, location_id: principal.id }));
      // Apply branding
      const root = document.documentElement;
      if (i.data.color_primary) root.style.setProperty('--primary', i.data.color_primary);
      if (i.data.color_secondary) root.style.setProperty('--secondary', i.data.color_secondary);
      if (i.data.color_accent) root.style.setProperty('--accent', i.data.color_accent);
      if (i.data.color_background) root.style.setProperty('--background', i.data.color_background);
      if (i.data.nombre) document.title = `${t('Reservar', 'Reservar')} • ${i.data.nombre}`;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('PublicReserve init failed:', err);
      }
    }
  }, [t]);

  useEffect(() => {
    init();
  }, [init]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/public/reservations', form);
      setSubmitted(r.data);
    } catch (err) {
      alert(t('Error al enviar la reserva', 'Erro ao enviar a reserva'));
    } finally {
      setBusy(false);
    }
  };

  if (!info) return null;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4" data-testid="public-reserve-success">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <div className="h-16 w-16 rounded-full bg-[hsl(var(--primary))]/15 grid place-items-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-[hsl(var(--primary))]" />
            </div>
            <h2 className="text-2xl font-heading font-bold">{t('¡Reserva recibida!', 'Reserva recebida!')}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{submitted.mensaje || t('Te confirmaremos en breve por teléfono o email.', 'Confirmaremos em breve por telefone ou email.')}</p>
            <div className="rounded-xl border bg-[hsl(var(--muted))]/30 p-3 text-sm space-y-1">
              <div><strong>{info.nombre}</strong></div>
              <div>{form.fecha} • {form.hora}h</div>
              <div>{form.personas} {t('personas', 'pessoas')}</div>
              <div>{form.cliente_nombre}</div>
            </div>
            <Button className="w-full" onClick={() => setSubmitted(null)}>{t('Hacer otra reserva', 'Fazer outra reserva')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4" data-testid="public-reserve-page">
      <Card className="max-w-md w-full overflow-hidden">
        <div
          className="h-32 relative"
          style={{
            background: `linear-gradient(135deg, hsl(${info.color_primary}), hsl(${info.color_accent}))`,
          }}
        >
          {info.logo_url && (
            <img src={info.logo_url} alt={info.nombre} className="absolute -bottom-8 left-6 h-16 w-16 rounded-2xl object-cover border-4 border-[hsl(var(--card))] shadow-md" />
          )}
        </div>
        <CardContent className="pt-12 pb-6 px-6 space-y-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">{info.nombre}</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('Reserva tu mesa online', 'Reserve sua mesa online')}</p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-[hsl(var(--muted-foreground))]">
              {info.direccion && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {info.direccion}</span>}
              {info.telefono && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {info.telefono}</span>}
            </div>
          </div>

          <form className="space-y-3" onSubmit={submit}>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{t('Fecha', 'Data')}</Label><Input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required min={new Date().toISOString().slice(0, 10)} data-testid="public-date-input" /></div>
              <div><Label className="text-xs">{t('Hora', 'Hora')}</Label>
                <Select value={form.hora} onValueChange={v => setForm({ ...form, hora: v })}>
                  <SelectTrigger data-testid="public-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['13:00','13:30','14:00','14:30','15:00','15:30','19:30','20:00','20:30','21:00','21:30','22:00','22:30','23:00'].map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">{t('Personas', 'Pessoas')}</Label>
              <div className="flex gap-1.5">
                {[1,2,3,4,5,6,7,8].map(n => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, personas: n })}
                    className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${form.personas === n ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]' : 'bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))]'}`}
                    data-testid={`public-people-${n}`}
                  >{n}{n === 8 ? '+' : ''}</button>
                ))}
              </div>
            </div>
            {locations.length > 1 && (
              <div>
                <Label className="text-xs">{t('Local', 'Local')}</Label>
                <Select value={form.location_id} onValueChange={v => setForm({ ...form, location_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.nombre}{loc.direccion ? ` • ${loc.direccion}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-xs">{t('Tu nombre', 'Seu nome')}</Label><Input value={form.cliente_nombre} onChange={e => setForm({ ...form, cliente_nombre: e.target.value })} required data-testid="public-name-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{t('Teléfono', 'Telefone')}</Label><Input value={form.cliente_telefono} onChange={e => setForm({ ...form, cliente_telefono: e.target.value })} required data-testid="public-phone-input" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.cliente_email} onChange={e => setForm({ ...form, cliente_email: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">{t('Notas (cumpleaños, alérgenos...)', 'Notas (aniversário, alérgenos...)')}</Label><Input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} /></div>
            <Button type="submit" disabled={busy || !form.cliente_nombre || !form.cliente_telefono} className="w-full" data-testid="public-submit-button">
              <CalendarCheck className="h-4 w-4 mr-1.5" />
              {busy ? '...' : t('Solicitar reserva', 'Solicitar reserva')}
            </Button>
          </form>
          <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center pt-2 border-t">
            {t('La reserva está sujeta a confirmación.', 'A reserva está sujeita a confirmação.')}
          </p>
        </CardContent>
        <div className="px-6 pb-3 text-center text-[10px] text-[hsl(var(--muted-foreground))]">
          <a href="https://www.tstt.com.br" target="_blank" rel="noopener" className="opacity-70 hover:opacity-100">© www.tstt.com.br</a>
        </div>
      </Card>
    </div>
  );
}
